<template>
    <div class="w-full">
        <button
            name="batch"
            data-test="batch"
            class="inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm font-medium text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none w-full"
            @click="open = true"
        >
            <ArchiveIcon class="h-6 w-6 mr-2" aria-hidden="true" />
            Batch
        </button>

        <TransitionRoot as="template" :show="open">
            <Dialog
                as="div"
                static
                class="fixed z-20 inset-0 overflow-y-auto"
                @close="open = false"
                :open="open"
            >
                <div
                    class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                        >
                            <div
                                class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                            >
                                <button
                                    type="button"
                                    class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                    @click="open = false"
                                >
                                    <span class="sr-only">Close</span>
                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <ArchiveIcon
                                        class="h-6 w-6 text-nutreance-green"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="mt-3 sm:mt-0">
                                    <DialogTitle
                                        as="h3"
                                        class="mt-6 sm:mt-2 text-xl leading-6 font-medium text-gray-800 sm:ml-4 text-center sm:text-left"
                                    >
                                        Batch Shipments
                                    </DialogTitle>

                                    <div class="mt-6 mb-4 mx-5 sm:mx-4">
                                        <p class="text-gray-500">
                                            Batching shipments will:
                                        </p>

                                        <ul
                                            class="my-4 mx-6 text-gray-500 list-disc"
                                        >
                                            <li>
                                                Delete any pending batches and
                                                rebatch any of their shipments
                                            </li>

                                            <li>
                                                Batch
                                                {{ quantityToBatch }} unbatched
                                                {{
                                                    quantityToBatch > 1
                                                        ? "shipments"
                                                        : "shipment"
                                                }}
                                            </li>

                                            <li>
                                                <label>
                                                    {{
                                                        includeProblemShipments
                                                            ? "Include"
                                                            : "Not include"
                                                    }}
                                                    problem shipments
                                                </label>
                                                <SwitchGroup
                                                    as="li"
                                                    class="flex mt-1 items-center text-transparent"
                                                >
                                                    <Switch
                                                        data-test="include_problem_shipments"
                                                        name="include_problem_shipments"
                                                        v-model="
                                                            includeProblemShipments
                                                        "
                                                        :class="[
                                                            includeProblemShipments
                                                                ? 'bg-nutreance-green'
                                                                : 'bg-gray-200',
                                                            'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none',
                                                        ]"
                                                    >
                                                        <span class="sr-only"
                                                            >Toggle Include
                                                            Problem
                                                            Shipments</span
                                                        >
                                                        <span
                                                            aria-hidden="true"
                                                            :class="[
                                                                includeProblemShipments
                                                                    ? 'translate-x-5'
                                                                    : 'translate-x-0',
                                                                'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                                            ]"
                                                        />
                                                    </Switch>
                                                </SwitchGroup>
                                            </li>
                                        </ul>

                                        <p class="text-gray-500">
                                            Are you sure you want to batch all
                                            shipments?
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <form
                                class="px-4 py-3 sm:px-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                                ref="form"
                                action="/user/shipment-batches"
                                method="POST"
                                @submit.prevent="onSubmit(form)"
                            >
                                <input
                                    type="hidden"
                                    name="_token"
                                    :value="csrf"
                                />
                                <input
                                    type="hidden"
                                    name="include_problem_shipments"
                                    :value="includeProblemShipments ? '1' : ''"
                                />

                                <button
                                    name="yes"
                                    type="submit"
                                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-nutreance-green text-base font-medium text-white hover:bg-nutreance-green-darker focus:outline-none sm:col-start-2 sm:text-sm"
                                >
                                    <LoadingIcon
                                        v-if="submitted"
                                        class="h-5 w-5 mr-2"
                                    />
                                    <CheckIcon
                                        v-else
                                        class="h-5 w-5 mr-2"
                                        aria-hidden="true"
                                    />
                                    Yes
                                </button>

                                <button
                                    name="no"
                                    type="button"
                                    class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 hover:bg-red-600 text-base font-medium text-white focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm"
                                    @click="open = false"
                                >
                                    <XIcon
                                        class="h-5 w-5 mr-2"
                                        aria-hidden="true"
                                    />
                                    No
                                </button>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    Switch,
    SwitchGroup,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { ArchiveIcon, CheckIcon, XIcon } from "@heroicons/vue/solid";
import { useForm } from "../../functions/useForm";
import LoadingIcon from "../LoadingIcon.vue";

defineProps<{
    csrf?: string;
    quantityToBatch: number;
}>();

const { onSubmit, submitted } = useForm();
const form = ref<any>(null);
const open = ref(false);
const includeProblemShipments = ref(true);
</script>
